/**
 * Admin route guard for /admin and other admin-only routes.
 * 
 * SECURITY: This is a UI guard only. Real admin authorization
 * must be enforced server-side via role validation.
 * 
 * TODO: Connect to real admin role from backend (see admin/backend role epic)
 * Currently uses demo predicate for development/demo purposes.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import { useAuthStore } from '@/store/authStore';
import { useIsAdmin } from '@/store/authStore';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Guard for admin-only routes.
 * 
 * Requirements:
 * - User must be authenticated
 * - User must have admin role (from backend or demo predicate)
 * 
 * Redirect behavior:
 * - Unauthenticated -> /login
 * - Authenticated but not admin -> / (home)
 * 
 * TODO: Replace demo predicate with real admin role check when
 * admin/backend role epic is implemented.
 */
export function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated } = useAuthStore();
  const isAdmin = useIsAdmin();
  const location = useLocation();

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to={RoutePaths.LOGIN} state={{ from: location }} replace />;
  }

  // Authenticated but not admin - redirect to home
  // Admin routes should never be treated as normal sponsor-gated content
  if (!isAdmin) {
    return <Navigate to={RoutePaths.HOME} replace />;
  }

  return <>{children}</>;
}